import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import {
  BookOpen,
  ChartBar,
  ClipboardListOutline,
  Magnify,
  Phone,
  ViewDashboard,
} from '@opengov/react-capital-assets';

import { useVcm } from '../contexts/VcmContext';
import { useAppNavigation } from '../hooks/useAppNavigation';

interface Tile {
  icon: React.ComponentType;
  label: string;
  description: string;
  path: string;
}

const TILES: Tile[] = [
  { icon: Magnify,             label: 'Client Search',    description: 'Find and manage veteran records',        path: 'clients/search'    },
  { icon: ClipboardListOutline, label: 'My Cases',         description: 'View your assigned caseload',            path: 'cases/my'          },
  { icon: ViewDashboard,       label: 'Dashboard',        description: 'Activity overview and alerts',            path: 'dashboard'         },
  { icon: Phone,               label: 'Call Center',      description: 'Log and manage incoming calls',           path: 'call-center'       },
  { icon: BookOpen,            label: 'Resource Library', description: 'Browse veteran service resources',        path: 'resources/library' },
  { icon: ChartBar,            label: 'Admin & Reports',  description: 'Analytics and organization data',         path: 'admin'             },
];

function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}

export function HomePage() {
  const { navigateToPath } = useAppNavigation();
  const { currentUser, currentEvent } = useVcm();

  const firstName = currentUser.name.split(' ')[0] ?? currentUser.name;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: (t) => t.palette.grey[50],
      }}
    >
      {/* ── Header band ──────────────────────────────────────────────── */}
      <Box
        component="header"
        sx={{
          bgcolor: (t) => t.palette.grey[100],
          borderBottom: 1,
          borderColor: 'divider',
          px: { xs: 3, md: 6 },
          py: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="overline"
            sx={{ color: 'text.secondary', fontSize: 10, letterSpacing: '0.12em', display: 'block', mb: 0.25 }}
          >
            OpenGov · Veterans Case Management
          </Typography>
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: 700, lineHeight: 1.2, color: 'text.primary' }}
          >
            Welcome back, {firstName}.
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {currentEvent.name}
          </Typography>
        </Box>

        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {formatDate(new Date())}
          </Typography>
        </Box>
      </Box>

      {/* ── Main content ─────────────────────────────────────────────── */}
      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 3, sm: 5, md: 8 },
          py: { xs: 4, md: 6 },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 860 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.1em', mb: 2, display: 'block' }}>
            Quick Access
          </Typography>
          <Grid container spacing={2}>
            {TILES.map((tile) => (
              <Grid key={tile.path} size={{ xs: 12, sm: 6, md: 4 }}>
                <QuickAccessTile
                  icon={tile.icon}
                  label={tile.label}
                  description={tile.description}
                  onClick={() => navigateToPath(tile.path)}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── Footer ───────────────────────────────────────────────────── */}
      <Box component="footer" sx={{ py: 2, px: 4 }}>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center' }}>
          Veterans Case Management System
        </Typography>
      </Box>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/*  QuickAccessTile                                                    */
/* ------------------------------------------------------------------ */

interface QuickAccessTileProps {
  icon: React.ComponentType;
  label: string;
  description: string;
  onClick: () => void;
}

function QuickAccessTile({ icon: Icon, label, description, onClick }: QuickAccessTileProps) {
  const theme = useTheme();

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        bgcolor: 'background.paper',
        transition: theme.transitions.create(['box-shadow', 'transform', 'border-color'], {
          duration: theme.transitions.duration.short,
        }),
        '&:hover': {
          boxShadow: theme.shadows[3],
          transform: 'translateY(-2px)',
          borderColor: theme.palette.primary.main,
        },
      }}
    >
      <CardActionArea onClick={onClick} sx={{ height: '100%', p: 3.5 }}>
        <Stack spacing={2}>
          {/* Icon */}
          <Box
            aria-hidden="true"
            sx={{
              width: 52,
              height: 52,
              borderRadius: 2,
              bgcolor: (t) => t.palette.primary.light,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: (t) => t.palette.primary.dark,
              '& svg': { width: 26, height: 26 },
            }}
          >
            <Icon />
          </Box>

          {/* Text */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
              {label}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              {description}
            </Typography>
          </Box>
        </Stack>
      </CardActionArea>
    </Card>
  );
}
